import { useUpload, FILE_STATUS } from '../context/UploadContext';
import { Progress } from './ui/progress';
import { 
  Upload, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  X,
  FileSpreadsheet,
  Pause,
  Play
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

export default function UploadStatusIndicator() {
  const { uploads, isUploading, clearUpload, getAllUploads, pauseUpload, resumeUpload } = useUpload();
  
  const allUploads = getAllUploads();
  
  if (allUploads.length === 0) {
    return null;
  }

  const activeUploads = allUploads.filter(u => 
    u.status === FILE_STATUS.UPLOADING || u.status === FILE_STATUS.PROCESSING
  );
  const pausedUploads = allUploads.filter(u => u.status === FILE_STATUS.PAUSED);
  const completedUploads = allUploads.filter(u => u.status === FILE_STATUS.COMPLETED);
  const errorUploads = allUploads.filter(u => u.status === FILE_STATUS.ERROR);

  // Calculate overall progress for active uploads
  const overallProgress = activeUploads.length > 0 
    ? Math.round(activeUploads.reduce((sum, u) => sum + u.progress, 0) / activeUploads.length)
    : 0;

  const getStatusIcon = (status) => {
    switch (status) {
      case FILE_STATUS.UPLOADING:
      case FILE_STATUS.PROCESSING:
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case FILE_STATUS.PAUSED:
        return <Pause className="w-4 h-4 text-yellow-500" />;
      case FILE_STATUS.COMPLETED:
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case FILE_STATUS.ERROR:
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileSpreadsheet className="w-4 h-4" />;
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant={isUploading ? "default" : "ghost"}
          size="sm" 
          className={`relative gap-2 ${isUploading ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          data-testid="upload-status-btn"
        >
          {isUploading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Upload className="w-4 h-4" />
          )}
          <span className="text-xs font-medium">
            {isUploading ? `Uploading ${overallProgress}%` : 
             pausedUploads.length > 0 ? `${pausedUploads.length} Paused` :
             `${allUploads.length} Upload(s)`}
          </span>
          {activeUploads.length > 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-400 rounded-full animate-pulse" />
          )}
          {errorUploads.length > 0 && !isUploading && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full" />
          )}
          {pausedUploads.length > 0 && !isUploading && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="p-3 border-b bg-muted/50">
          <h4 className="font-semibold text-sm">File Uploads</h4>
          <p className="text-xs text-muted-foreground">
            {activeUploads.length > 0 && `${activeUploads.length} uploading`}
            {pausedUploads.length > 0 && ` • ${pausedUploads.length} paused`}
            {completedUploads.length > 0 && ` • ${completedUploads.length} completed`}
            {errorUploads.length > 0 && ` • ${errorUploads.length} failed`}
          </p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {allUploads.map((upload) => (
            <div 
              key={upload.id} 
              className={`p-3 border-b last:border-0 ${
                upload.status === FILE_STATUS.ERROR ? 'bg-red-500/5' :
                upload.status === FILE_STATUS.COMPLETED ? 'bg-green-500/5' :
                upload.status === FILE_STATUS.PAUSED ? 'bg-yellow-500/5' :
                upload.status === FILE_STATUS.UPLOADING ? 'bg-blue-500/5' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {getStatusIcon(upload.status)}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{upload.fileName}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatSize(upload.fileSize)}
                      {upload.status === FILE_STATUS.UPLOADING && (
                        <> • Chunk {upload.chunksUploaded}/{upload.chunksTotal}</>
                      )}
                      {upload.status === FILE_STATUS.PAUSED && (
                        <> • Paused at chunk {upload.chunksUploaded}/{upload.chunksTotal}</>
                      )}
                      {upload.status === FILE_STATUS.COMPLETED && (
                        <> • {upload.result?.processed?.toLocaleString() || 0} records</>
                      )}
                      {upload.status === FILE_STATUS.ERROR && (
                        <> • {upload.error}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Pause button for uploading */}
                  {upload.status === FILE_STATUS.UPLOADING && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={() => pauseUpload(upload.id)}
                      title="Pause upload"
                    >
                      <Pause className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Resume button for paused */}
                  {upload.status === FILE_STATUS.PAUSED && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-green-500 hover:text-green-600"
                      onClick={() => resumeUpload(upload.id)}
                      title="Resume upload"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Retry button for errors */}
                  {upload.status === FILE_STATUS.ERROR && upload.file && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7 text-blue-500 hover:text-blue-600"
                      onClick={() => resumeUpload(upload.id)}
                      title="Retry upload"
                    >
                      <Play className="w-4 h-4" />
                    </Button>
                  )}
                  {/* Clear button for completed/error */}
                  {(upload.status === FILE_STATUS.COMPLETED || upload.status === FILE_STATUS.ERROR || upload.status === FILE_STATUS.PAUSED) && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7"
                      onClick={() => clearUpload(upload.id)}
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              {/* Progress bar with percentage */}
              {(upload.status === FILE_STATUS.UPLOADING || upload.status === FILE_STATUS.PAUSED) && (
                <div className="mt-2">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{upload.progress}%</span>
                  </div>
                  <Progress 
                    value={upload.progress} 
                    className={`h-2 ${upload.status === FILE_STATUS.PAUSED ? 'opacity-60' : ''}`} 
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {allUploads.length === 0 && (
          <div className="p-4 text-center text-muted-foreground text-sm">
            No uploads
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
